import { baseApi } from '@/shared/api/baseApi';
import { credentialsUpdated, loggedOut, sessionCleared } from '@/shared/lib/redux/actions';
import type { AuthResponse, MembershipSummary, UserProfile } from '../model/types';

export interface RegisterRequest {
  login: string;
  password: string;
  name: string;
  phone: string;
}

export interface LoginRequest {
  login: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateMeRequest {
  name?: string;
  phone?: string;
}

export interface MessageResponse {
  message: string;
}

const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (body) => ({ url: 'auth/register', method: 'POST', body }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(credentialsUpdated(data));
      },
    }),
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (body) => ({ url: 'auth/login', method: 'POST', body }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(credentialsUpdated(data));
      },
      invalidatesTags: ['Me'],
    }),
    /** Bootstrap-only — the httpOnly `rt` cookie carries the session; a normal 401-triggered
     *  refresh happens inside baseApi's own reauth wrapper, not through this hook. */
    refresh: builder.mutation<AuthResponse, void>({
      query: () => ({ url: 'auth/refresh', method: 'POST' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(credentialsUpdated(data));
        } catch {
          dispatch(sessionCleared());
        }
      },
    }),
    logout: builder.mutation<MessageResponse, void>({
      query: () => ({ url: 'auth/logout', method: 'POST' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => undefined);
        dispatch(loggedOut());
        dispatch(sessionCleared());
      },
    }),
    logoutAll: builder.mutation<MessageResponse, void>({
      query: () => ({ url: 'auth/logout-all', method: 'POST' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => undefined);
        dispatch(loggedOut());
        dispatch(sessionCleared());
      },
    }),
    changePassword: builder.mutation<MessageResponse, ChangePasswordRequest>({
      query: (body) => ({ url: 'auth/change-password', method: 'POST', body }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => undefined);
        dispatch(sessionCleared());
      },
    }),

    getMe: builder.query<UserProfile, void>({
      query: () => 'me',
      providesTags: ['Me'],
    }),
    updateMe: builder.mutation<UserProfile, UpdateMeRequest>({
      query: (body) => ({ url: 'me', method: 'PATCH', body }),
      invalidatesTags: ['Me'],
    }),
    /** §3.6, §9: мои магазины (владелец/клиент) и статусы заявок на подключение. */
    getMemberships: builder.query<MembershipSummary[], void>({
      query: () => 'me/memberships',
      providesTags: ['Memberships'],
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useRefreshMutation,
  useLogoutMutation,
  useLogoutAllMutation,
  useChangePasswordMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useUpdateMeMutation,
  useGetMembershipsQuery,
} = authApi;

export { authApi };
